/**
 * TrackLabel Component (Task 3.2)
 *
 * Displays a single track label with delete functionality.
 *
 * Features:
 * - Track label text aligned with 60px track row height
 * - Delete button (X icon) appears on hover
 * - Delete button uses subtle gray styling, turns red on hover
 * - Double-click to edit track name
 * - Framer Motion for hover state animations
 *
 * Props:
 * - trackNumber: The track number (1-based)
 * - label: The track label text
 * - onDelete: Callback when delete button is clicked
 * - onRename: Callback when track name is changed
 * - canDelete: Whether the delete button should be shown
 */

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface TrackLabelProps {
  /**
   * Track number (1-based index)
   */
  trackNumber: number;

  /**
   * Track label text to display
   */
  label: string;

  /**
   * Callback when delete button is clicked
   */
  onDelete: () => void;

  /**
   * Callback when track name is changed via double-click edit
   */
  onRename?: (newLabel: string) => void;

  /**
   * Whether the track can be deleted (false if last track)
   */
  canDelete: boolean;
}

export function TrackLabel({
  trackNumber,
  label,
  onDelete,
  onRename,
  canDelete,
}: TrackLabelProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const handleDoubleClick = () => {
    if (onRename) {
      setEditValue(label);
      setIsEditing(true);
    }
  };

  const handleEditSubmit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== label && onRename) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditValue(label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSubmit();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  return (
    <motion.div
      className="relative flex items-center px-2"
      style={{
        height: '60px',
        borderBottom: '1px solid var(--color-dark-border)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={handleDoubleClick}
      data-testid={`track-label-${trackNumber}`}
    >
      {/* Track label text or edit input */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleEditSubmit}
          onKeyDown={handleKeyDown}
          className="w-full bg-transparent text-xs font-medium outline-none border-b border-blue-500"
          style={{ color: 'var(--color-dark-text)' }}
          maxLength={20}
        />
      ) : (
        <span
          className="text-xs font-medium select-none truncate cursor-default"
          style={{ color: 'var(--color-dark-text-muted)' }}
          title={onRename ? 'Double-click to rename' : undefined}
        >
          {label}
        </span>
      )}

      {/* Delete button - appears on hover (not during edit) */}
      {canDelete && !isEditing && (
        <motion.button
          className="absolute right-1 flex items-center justify-center rounded p-1"
          style={{
            backgroundColor: 'transparent',
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: isHovered ? 1 : 0,
            pointerEvents: isHovered ? 'auto' : 'none',
          }}
          whileHover={{
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
          }}
          transition={{ duration: 0.15 }}
          onClick={handleDeleteClick}
          aria-label={`Delete ${label}`}
          data-testid={`delete-track-${trackNumber}`}
        >
          <motion.div
            initial={{ color: '#6b7280' }}
            whileHover={{ color: '#ef4444' }}
            transition={{ duration: 0.15 }}
          >
            <X className="h-3.5 w-3.5" />
          </motion.div>
        </motion.button>
      )}
    </motion.div>
  );
}
